import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Upload, Trash2, ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";

const ROTATIONS = ["-2deg", "1.5deg", "-1deg", "2.5deg", "0.5deg", "-2.5deg"];

export default function Memories({ couple, slot, me }) {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [viewer, setViewer] = useState(null);

  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [memDate, setMemDate] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/couples/${couple.id}/memories`);
      setMemories(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [couple.id]); // eslint-disable-line

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image.");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a photo first.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("memory_date", memDate);
      fd.append("from_slot", slot);
      await api.post(`/couples/${couple.id}/memories`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Memory added to your wall 📸");
      setOpen(false); setFile(null); setPreview(null); setCaption(""); setMemDate("");
      load();
    } catch {
      toast.error("Upload failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setMemories((m) => m.filter((x) => x.id !== id));
    setViewer(null);
    try { await api.delete(`/couples/${couple.id}/memories/${id}`); } catch { load(); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-primary">Our little gallery</div>
          <h1 className="font-serif text-3xl font-semibold">Memory wall</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-memory-btn" size="sm" className="rounded-full gap-1">
              <Plus className="h-4 w-4" /> Add
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Add a memory</DialogTitle>
              <DialogDescription className="sr-only">Upload a photo to your memory wall</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <input ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid="memory-file-input" />
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full aspect-video rounded-2xl border-2 border-dashed border-border bg-secondary/40 flex flex-col items-center justify-center gap-2 overflow-hidden"
              >
                {preview ? (
                  <img src={preview} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <>
                    <Upload className="h-7 w-7 text-primary" />
                    <span className="text-xs text-muted-foreground">Tap to choose a photo</span>
                  </>
                )}
              </button>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Caption</Label>
                <Input data-testid="memory-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Our favorite day…" className="rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Date</Label>
                <Input data-testid="memory-date-input" type="date" value={memDate} onChange={(e) => setMemDate(e.target.value)} className="rounded-xl" />
              </div>
              <Button data-testid="upload-memory-btn" disabled={busy} onClick={upload} className="w-full rounded-full h-11 font-semibold">
                {busy ? "Uploading…" : "Pin to wall"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground">Loading…</div>
      ) : memories.length === 0 ? (
        <div className="py-16 text-center" data-testid="memories-empty">
          <div className="h-16 w-16 rounded-full bg-accent flex items-center justify-center mx-auto mb-4">
            <ImageIcon className="h-7 w-7 text-primary" />
          </div>
          <p className="font-serif text-xl">No memories yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">Pin your favorite photos here — every moment worth remembering.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {memories.map((m, i) => (
            <motion.button
              key={m.id} data-testid={`memory-${m.id}`}
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.04 }}
              whileHover={{ rotate: 0, scale: 1.02 }}
              style={{ rotate: ROTATIONS[i % ROTATIONS.length] }}
              onClick={() => setViewer(m)}
              className="bg-white p-2 pb-3 rounded-sm shadow-md"
            >
              <img
                src={`${API}/files/${m.storage_path}`} alt={m.caption || "memory"}
                className="w-full aspect-square object-cover rounded-sm"
              />
              <div className="pt-2 px-0.5">
                <div className="font-serif text-sm text-stone-800 truncate leading-tight">{m.caption || "Untitled"}</div>
                {m.memory_date && <div className="text-[10px] text-stone-500">{new Date(m.memory_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>}
              </div>
            </motion.button>
          ))}
        </div>
      )}

      {/* viewer */}
      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-sm rounded-3xl p-0 overflow-hidden">
          {viewer && (
            <div>
              <DialogHeader className="sr-only">
                <DialogTitle>{viewer.caption || "Memory"}</DialogTitle>
                <DialogDescription>Memory photo detail</DialogDescription>
              </DialogHeader>
              <img src={`${API}/files/${viewer.storage_path}`} alt={viewer.caption} className="w-full object-cover max-h-[60vh]" />
              <div className="p-4">
                <div className="font-serif text-xl">{viewer.caption || "Untitled"}</div>
                {viewer.memory_date && <div className="text-xs text-muted-foreground mt-0.5">{new Date(viewer.memory_date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</div>}
                <Button data-testid="delete-memory-btn" variant="ghost" onClick={() => remove(viewer.id)} className="mt-3 text-destructive hover:text-destructive gap-1 rounded-full">
                  <Trash2 className="h-4 w-4" /> Remove
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
